import {AbstractEngine} from './AbstractEngine';
import {BaseAnimation} from '../animations/BaseAnimation';

type Step={
    value:any;offset:number
};

class JQueryEngine extends AbstractEngine {

    declare protected animation:BaseAnimation;
    protected queue:string='';

    constructor(animation:BaseAnimation) {
        super(animation);
    }

    public static getClassName():string {
        return 'JQueryEngine';
    }

    private bezCoOrd(t: number, ax: number, p1: number[], p2: number[]): number {
        const C = 3 * p1[ax];
        const B = 3 * (p2[ax] - p1[ax]) - C;
        const A = 1 - C - B;
        return t * (C + t * (B + t * A));
    }

    private xDeriv(t: number, p1: number[], p2: number[]): number {
        const C = 3 * p1[0];
        const B = 3 * (p2[0] - p1[0]) - C;
        const A = 1 - C - B;
        return C + t * (2 * B + 3 * A * t);
        }

    private xForT(t: number, p1: number[], p2: number[]): number {
        let x = t;
        let i = 0;
                  while (++i < 14) {
            const z = this.bezCoOrd(x, 0, p1, p2) - t;
                    if (Math.abs(z) < 1e-3) break;
            x -= z / this.xDeriv(x, p1, p2);
                  }
                  return x;
            }

    // See: https://github.com/rdallasgray/bez
    private createBezierEasing(coOrdArray: number[]): string {
        const encodedFuncName = 'bez_' + coOrdArray.join('_').replace(/\./g, 'p');
        if (typeof $.easing[encodedFuncName] !== 'function') {
            const p1: number[] = [coOrdArray[0], coOrdArray[1]];
            const p2: number[] = [coOrdArray[2], coOrdArray[3]];
            $.easing[encodedFuncName] = ((_x: number, t: number, b: number, c: number, d: number) => {
                return c * this.bezCoOrd(this.xForT(t / d, p1, p2), 1, p1, p2) + b;
            }) as any;
          }
        return encodedFuncName;
    }

    public seek(pos:number):BaseAnimation {
        throw 'Seek not implemented for JQueryEngine';
    }

    public scrub(pos:number):BaseAnimation {
        throw 'Scrub not implemented for JQueryEngine';
    }

    private camelify(prop:string) {
        return prop.replace(/-([a-z])/g,(g)=>g[1].toUpperCase());
    }

    private getSteps(anim:any):Step[] {
        var steps=[];
        var apro=this.camelify(anim.property);
        for (var [offset,val] of Object.entries(anim.steps)) {
            steps.push({value:val,offset:parseFloat(offset)/100});
        }
        steps.sort((a,b)=>a.offset-b.offset);
        return steps;
    }

    private convertEasing(easing:string) {
        switch (easing) {
            case 'ease': return this.createBezierEasing([0.25, 0.1, 0.25, 1.0]);
            case 'ease-in': return this.createBezierEasing([0.42, 0.0, 1.0, 1.0]);
            case 'ease-in-out': return this.createBezierEasing([0.42, 0.0, 0.58, 1.0]);
            case 'ease-out': return this.createBezierEasing([0.0, 0.0, 0.58, 1.0]);
        }

        var ce=this.camelify(easing);
        if (typeof $.easing[ce]==='undefined') throw(`Easing "${ce}" not supported by JQuery.`);
        return ce;
    }

    private getOptions(anim:any) {
        var animatedElement=this.animation.getAnimatedElement();
        var opts={
            easing: this.convertEasing(anim.easing),
            duration: anim.duration,
            step: (now:number,tween:any)=>{
                this.animation.setCSS(animatedElement,tween.prop,String(now));
                this.animation.trigger('step',{tween});
            }
        };
        return opts;
    }

    private getUUID() {
        return Math.random().toString(36).slice(2);
    }

    public stop():BaseAnimation {
        var animatedElement=this.animation.getAnimatedElement();
        animatedElement.stop(this.queue);
        this.animation.trigger('stop');
        return this.animation;
    }

    public play():BaseAnimation {
        this.animation.trigger('begin');
        var animatedElement=this.animation.getAnimatedElement();
        var animations=this.animation.getAnimations();
        var promises=[];
        this.queue='queue_'+this.getUUID();

        for (var anim of animations) {
            var apro=this.camelify(anim.property);
            var steps=this.getSteps(anim);
            var opts:any=this.getOptions(anim);
            if (steps.length!=2) continue;
            var from=steps[0];
            var to=steps[1];
            this.animation.setCSS(animatedElement,apro,from.value);
            opts.queue=this.queue;
            let promise=new Promise((resolve,reject)=>{
                opts.done=()=>{
                    resolve(true);
                };
            });
            animatedElement.animate(
                {[apro]: to.value},
                opts
            ).dequeue(this.queue);
            promises.push(promise);
        }
        Promise.all(promises).then(
            ()=>{
                 this.animation.trigger('end');
             }
        );
        return this.animation;
    }

    static {
        this.init();
}

}

export {};